import React, { useCallback } from 'react';
import { ConfigProvider, Table as AntdTable } from 'antd';
import * as AntdTableInterface from 'antd/es/table';

import { isEven } from '@totem/utilities/numberUtilities';

import './IBTable.css';

export interface TableProps<
  T extends object,
> extends AntdTableInterface.TableProps<T> {
  // Rendered bottom-left, in the same row as pagination (which antd always
  // right-aligns) — e.g. an Export button. Omit for the default antd layout.
  footerLeft?: React.ReactNode;
}

function IBTable<T extends object>(props: TableProps<T>) {
  const {
    dataSource,
    columns,
    pagination,
    loading,
    onChange,
    rowClassName = '',
    footerLeft,
    ...restProps
  } = props;

  const getPopupContainer = useCallback(() => document.body, []);

  const getRowClassName = useCallback(
    (record: T, index: number, indent: number) => {
      const className = isEven(index)
        ? 'totem-table-row-even'
        : 'totem-table-row-odd';

      const additionalClassName =
        typeof rowClassName === 'string'
          ? rowClassName
          : rowClassName(record, index, indent);

      return `${className} ${additionalClassName}`;
    },
    [rowClassName],
  );

  const table = (
    <AntdTable
      {...restProps}
      bordered
      columns={columns}
      className="totem-table"
      size="small"
      scroll={{ x: '100%' }}
      rowClassName={getRowClassName}
      loading={loading}
      dataSource={dataSource}
      pagination={pagination}
      onChange={onChange}
    />
  );

  if (!footerLeft) {
    return (
      <ConfigProvider getPopupContainer={getPopupContainer}>
        {table}
      </ConfigProvider>
    );
  }

  return (
    <ConfigProvider getPopupContainer={getPopupContainer}>
      <div styleName="table-wrapper">
        {table}
        <div styleName="footer-left">{footerLeft}</div>
      </div>
    </ConfigProvider>
  );
}

export default IBTable;
